
import { Component, Vue, Prop, Watch, Ref } from 'vue-property-decorator';
import {TakipTalebiEntity} from "@/entity/TakipTalebiEntity";

interface VariableInfo {
  full: string;
  name: string;
  startPos: number;
  endPos: number;
}

@Component
export default class TakipTalebiDegistirme extends Vue {
  @Prop({ type: String, default: '' }) readonly value!: string;
  @Prop() takipTalebi!:TakipTalebiEntity
  @Ref('textareaEl') readonly textareaEl!: any;

  metinDisabled = true
  textContent = '';
  originalContent = '';
  initialEditContent = '';
  lastValidContent = '';
  variables: VariableInfo[] = [];
  variableRegex = /\{\{([^}]+)\}\}/g;
  isProcessing = false;
  isFirstFocus = true;
  isMounted = false;

  mounted() {
    this.isMounted = true;
    this.initializeContent();
  }

  initializeContent() {
    // Önce value prop'unu kontrol et
    if (this.value) {
      this.textContent = this.value;
      this.originalContent = this.value;
      this.lastValidContent = this.value;
    }
    // Sonra takipTalebi içeriğini kontrol et
    else if (this.takipTalebi) {
      if (this.takipTalebi.ozel_takip_talebi_metni) {
        this.textContent = this.takipTalebi.ozel_takip_talebi_metni;
        this.originalContent = this.takipTalebi.ozel_takip_talebi_metni;
        this.lastValidContent = this.takipTalebi.ozel_takip_talebi_metni;
      } else if (this.takipTalebi.takip_alt_tipi?.takip_talebi_metni) {
        const defaultMetin = this.takipTalebi.takip_alt_tipi.takip_talebi_metni;
        this.textContent = defaultMetin;
        this.originalContent = defaultMetin;
        this.lastValidContent = defaultMetin;

        // Parent component'e değişikliği bildir
        this.$nextTick(() => {
          if (this.takipTalebi.takip_alt_tipi) {
            this.takipTalebi.takip_alt_tipi.ozel_takip_talebi_metni = defaultMetin;
          }
          this.$emit('input', defaultMetin);
        });
      }
    }

    this.findVariables();
  }

  @Watch('value', { immediate: true })
  onValueChange(newVal: string): void {
    if (!this.isProcessing && this.isMounted && newVal !== this.textContent) {
      this.originalContent = newVal || '';
      this.textContent = this.originalContent;
      this.lastValidContent = this.originalContent;
      this.findVariables();
    }
  }

  @Watch('takipTalebi', { deep: true, immediate: true })
  onTakipTalebiChange(): void {
    if (this.isMounted && !this.textContent && !this.value) {
      this.initializeContent();
    }
  }

  metinDuzenle() {
    this.metinDisabled = !this.metinDisabled;
  }

  findVariables(): void {
    this.variables = [];
    const content = this.originalContent || this.textContent;
    if (!content) return;

    let match;
    this.variableRegex.lastIndex = 0;
    while ((match = this.variableRegex.exec(content)) !== null) {
      this.variables.push({
        full: match[0],
        name: match[1],
        startPos: match.index,
        endPos: match.index + match[0].length
      });
    }
  }

  handleFocus(): void {
    if (this.isFirstFocus) {
      this.initialEditContent = this.textContent;
      this.isFirstFocus = false;
    }
  }

  handleTextChange(): void {
    if (this.isProcessing) return;

    this.isProcessing = true;

    const currentContent = this.textContent;
    let isValid = true;

    for (const variable of this.variables) {
      if (!currentContent.includes(variable.full)) {
        isValid = false;
        break;
      }
    }

    if (isValid) {
      this.lastValidContent = currentContent;
      this.$emit('input', currentContent);

      // takipTalebi güncellemesi
      if (this.takipTalebi) {
        this.takipTalebi.ozel_takip_talebi_metni = currentContent;
        if (this.takipTalebi.takip_alt_tipi) {
          this.takipTalebi.takip_alt_tipi.ozel_takip_talebi_metni = currentContent;
        }
      }
    } else {
      this.textContent = this.lastValidContent;

      this.$nextTick(() => {
        const textarea = this.textareaEl?.$refs?.input;
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(this.textContent.length, this.textContent.length);
        }
      });
    }

    this.isProcessing = false;
  }

  handleKeyDown(event: KeyboardEvent): void {
    const target = event.target as HTMLTextAreaElement;
    const selStart = target.selectionStart;
    const selEnd = target.selectionEnd;

    if ((event.key === 'Backspace' || event.key === 'Delete') &&
        this.isSelectionInVariableRegion(selStart, selEnd)) {
      event.preventDefault();
      return;
    }

    if (event.key.length === 1 && this.isSelectionInVariableRegion(selStart, selEnd)) {
      event.preventDefault();
      return;
    }
  }

  isSelectionInVariableRegion(start: number | null, end: number | null): boolean {
    if (start === null || end === null) return false;

    if (start === end) {
      for (const variable of this.variables) {
        if (this.getVariablePositionInCurrentText(variable, start)) {
          return true;
        }
      }
    } else {
      for (const variable of this.variables) {
        const pos: any = this.getVariablePositionInCurrentText(variable);
        if (!pos) continue;

        if ((start < pos.end && end > pos.start)) {
          return true;
        }
      }
    }

    return false;
  }

  getVariablePositionInCurrentText(variable: VariableInfo, position: number | null = null): {start: number, end: number} | boolean | null {
    const index = this.textContent.indexOf(variable.full);
    if (index === -1) return null;

    const start = index;
    const end = index + variable.full.length;

    if (position !== null) {
      return position >= start && position <= end;
    }

    return { start, end };
  }
}
